import { useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "./i18n/LanguageContext";
import { normalizeRole, getRoleLabel } from "./utils/roleUtils";
import { isAdminEmail } from "./config/admins";

function Auth() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("partybuddy");
  const [rememberMe, setRememberMe] = useState(true);
  const { language, t } = useTranslation();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Preserve the originally requested URL (e.g. a shared /event/:eventId
  // link) across the login/registration round trip. Carried via router
  // state (set by ProtectedRoute/AppRoutes when redirecting here) rather
  // than a query param, so nothing sensitive ends up in the URL.
  const from = location.state?.from;
  const redirectTarget = from ? `${from.pathname}${from.search || ""}${from.hash || ""}` : "/";

  // Applies the "Stay logged in" choice to Firebase Auth's actual session
  // persistence (not just UI state) before the sign-in/registration call
  // that starts the new session, per Firebase's documented requirement to
  // set persistence before signInWithEmailAndPassword.
  const applyPersistence = () =>
    setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

  const getAuthErrorMessage = (err, mode) => {
    switch (err?.code) {
      case "auth/email-already-in-use":
        return t("auth.emailInUse", "This email address is already in use.");
      case "auth/invalid-email":
        return t("auth.invalidEmail", "Please enter a valid email address.");
      case "auth/weak-password":
        return t("auth.weakPassword", "Your password is too weak.");
      case "auth/invalid-credential":
      case "auth/user-not-found":
      case "auth/wrong-password":
        return mode === "login"
          ? t("auth.invalidCredentials", "Incorrect email or password.")
          : t("auth.registerError", "Registration failed");
      case "auth/too-many-requests":
        return t("auth.tooManyRequests", "Too many attempts. Please try again later.");
      case "auth/network-request-failed":
        return t("auth.networkError", "Network error. Please check your connection and try again.");
      default:
        return mode === "login"
          ? t("auth.loginError", "Sign-in failed")
          : t("auth.registerError", "Registration failed");
    }
  };

  const handleRegister = async () => {
    try {
      await applyPersistence();
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const uid = cred.user.uid;

      const savedRole = normalizeRole(role);
      // Admin is a separate permission flag, not a replacement for the role.
      const adminFlag = isAdminEmail(email);
      const finalUsername = username || (adminFlag ? email.split("@")[0] : username);
      await setDoc(doc(db, "users", uid), {
        email,
        username: finalUsername,
        role: savedRole,
        admin: adminFlag,
        friends: [],
        following: [],
        followers: [],
        incomingRequests: [],
        outgoingRequests: [],
      });

      setError("");
      setMessage(`✅ ${t('auth.registerSuccess', 'Registration successful!')}`);
      setTimeout(() => {
        setMessage("");
        navigate(redirectTarget, { replace: true });
      }, 2000);
    } catch (err) {
      setError(`❌ ${getAuthErrorMessage(err, "register")}`);
    }
  };

  const handleLogin = async () => {
    try {
      await applyPersistence();
      await signInWithEmailAndPassword(auth, email, password);
      setError("");
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setError(`❌ ${getAuthErrorMessage(err, "login")}`);
    }
  };

  const openResetModal = () => {
    setResetEmail(email);
    setResetMessage("");
    setShowReset(true);
  };

  const closeResetModal = () => {
    setShowReset(false);
    setResetMessage("");
  };

  const handlePasswordReset = async () => {
    if (isResetting) return;
    setResetMessage("");

    if (!resetEmail.trim()) {
      setResetMessage(`⚠️ ${t("auth.resetEmailRequired", "Please enter your email first!")}`);
      return;
    }

    setIsResetting(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
      setResetMessage(`✅ ${t("auth.resetSuccess", "Password reset email sent. Please check your inbox.")}`);
    } catch (err) {
      // Map known Firebase error codes to user-friendly text; never surface raw Firebase messages.
      if (err.code === "auth/invalid-email") {
        setResetMessage(`❌ ${t("auth.resetInvalidEmail", "Please enter a valid email address.")}`);
      } else if (err.code === "auth/too-many-requests") {
        setResetMessage(`❌ ${t("auth.resetTooManyRequests", "Too many attempts. Please try again later.")}`);
      } else {
        setResetMessage(`❌ ${t("auth.resetError", "Password reset failed. Please check your email address and try again.")}`);
      }
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-950/70 p-6">
      <div className="glass-card flex flex-col gap-6 p-8 max-w-md w-full text-white rounded-[32px] shadow-2xl border border-slate-700/60">
        <h2 className="text-4xl font-bold text-center">{t('auth.title', 'Login')}</h2>

        {error && (
          <div className="bg-rose-900/70 border border-rose-700 text-rose-200 p-3 rounded-xl text-sm font-medium">{error}</div>
        )}

        {message && (
          <div className="bg-green-900/50 border border-green-700 text-green-200 p-3 rounded-lg text-sm font-medium">{message}</div>
        )}

        <input
          className="bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
          placeholder={t('auth.username', 'Username')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />

        <input
          className="bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
          placeholder={t('auth.email', 'E-mail')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          className="bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
          placeholder={t('auth.password', 'Password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <select
          className="bg-slate-700 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <option value="partybuddy">{getRoleLabel('partybuddy', language)}</option>
          <option value="artist">{getRoleLabel('artist', language)}</option>
          <option value="organizer">{getRoleLabel('organizer', language)}</option>
        </select>

        <label className="flex select-none items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-2 focus:ring-blue-400"
          />
          {t('auth.rememberMe', 'Stay logged in')}
        </label>

        <button onClick={handleRegister} className="w-full primary-action px-3 py-3 rounded-2xl font-semibold transition-all duration-200 shadow-xl hover:shadow-2xl">
          {t('auth.register', 'Register')}
        </button>
        <button onClick={handleLogin} className="w-full bg-slate-700 border border-slate-600 px-3 py-3 rounded-2xl font-semibold transition-all duration-200 hover:bg-slate-600 shadow-lg">
          {t('auth.login', 'Log in')}
        </button>

        <p
          className="text-sm text-blue-400 cursor-pointer hover:text-blue-300 hover:underline transition text-center"
          onClick={openResetModal}
        >
          {t('auth.forgotPassword', 'Forgot password?')}
        </p>
      </div>

      {showReset && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          onClick={closeResetModal}
        >
          <div
            className="glass-card flex flex-col gap-4 p-6 max-w-sm w-full text-white rounded-[28px] shadow-2xl border border-slate-700/60"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-center">{t('auth.resetTitle', 'Reset password')}</h3>
            <p className="text-sm text-gray-300">
              {t('auth.resetInstructions', 'Enter your email address to receive a password reset link.')}
            </p>

            {resetMessage && (
              <div
                className={`p-3 rounded-lg text-sm font-medium border ${
                  resetMessage.includes('❌')
                    ? 'bg-rose-900/70 border-rose-700 text-rose-200'
                    : resetMessage.includes('⚠️')
                    ? 'bg-yellow-900/50 border-yellow-700 text-yellow-200'
                    : 'bg-green-900/50 border-green-700 text-green-200'
                }`}
              >
                {resetMessage}
              </div>
            )}

            <input
              className="bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
              placeholder={t('auth.email', 'E-mail')}
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
            />

            <div className="flex gap-3">
              <button
                onClick={handlePasswordReset}
                disabled={isResetting}
                className="flex-1 primary-action px-3 py-3 rounded-2xl font-semibold transition-all duration-200 shadow-xl hover:shadow-2xl disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isResetting ? t('auth.resetSending', 'Sending...') : t('auth.resetButton', 'Send reset link')}
              </button>
              <button
                onClick={closeResetModal}
                className="flex-1 bg-slate-700 border border-slate-600 px-3 py-3 rounded-2xl font-semibold transition-all duration-200 hover:bg-slate-600 shadow-lg"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Auth;